import { Level } from "interfaces/models";
import { DataProjectModel } from "interfaces/models/dataProjectModel";
import CacheService from "./classes/CacheService";

export enum CacheProjectStatus {
  PROCESSING = "processing",
  ERROR = "error",
  FINISHED = "finished",
  PAUSED = "paused",
  UNCACHED = "uncached",
  STOPPING = "stopping",
  IS_CLEARING = "is_clearing",
}

export interface ICacheStorageState {
  status: CacheProjectStatus;
  lastUpdated: number | null;
  keys: string[]; // cached keys
  resources: ProjectResourceInfo[];
}

/**
 * Class to save data to IndexDB
 */
export interface ICacheStorage {
  get(): Promise<ICacheStorageState>;

  save(state: ICacheStorageState): Promise<void>;
}

/**
 * Class to manage cache progress of a project
 */
export interface ICacheModel {
  setStatus(status: CacheProjectStatus): void;

  setKeyCached(key: string): void;

  isCached(key: string): boolean;

  reset(): Promise<void>;

  /**
   * Load cache status from IndexDB
   */
  load(): Promise<void>;

  /**
   * Get list of cached keys
   * @returns list of cached keys
   */
  getKeysCached(): string[];

  /**
   * @returns lastUpdated
   */
  getLastUpdated(): number | null;

  getStatus(): CacheProjectStatus;

  /**
   * Save cache status to IndexDB
   */
  save(): Promise<void>;
}

export interface ICacheProject extends ICacheModel {
  /**
   * Called when there are cache changes
   * 1. First go to store to get list of cached keys
   * 2. Then compare with registered keys to calculate cache percentage
   * 3. Return cache percentage
   * @returns cache percentage
   */
  getProgress(): number;
}

export type OnProgress = (progress: number) => void;

export type OnCallback = (progress: number) => void;

/**
 * Class to manage all projects
 */
export interface ICacheProjectManager {
  start(id: string): Promise<void>;

  pause(id: string): void;

  restart(id: string): Promise<void>;

  clear(id: string): Promise<void>;
}

export interface CacheContext {
  versionId: string;
  bimFileId: string;
  project: DataProjectModel;
  cachedUrls: string[];
  version: string;
}

export interface IRegisterCacheModule {
  key: string;
  handler(ctx: CacheContext, cacheService: CacheService): Promise<any>;
}

export type CacheListenerData = {
  projectId: string;
  status: CacheProjectStatus;
  lastUpdated: number;
  progress: number;
};

export type CacheListener = (data: CacheListenerData[]) => void;

export const defaultCacheListenerData: CacheListenerData = {
  projectId: "",
  status: CacheProjectStatus.UNCACHED,
  lastUpdated: 0,
  progress: 0,
};

export const errorCodes = {
  NOT_GENERATED: "NOT_GENERATED",
  PROCESSING: "PROCESSING",
  PROCESSING_OTHER_PROJECT: "PROCESSING_OTHER_PROJECT",
  CACHE_DONE: "CACHE_DONE",
  PROJECT_NOT_FOUND: "PROJECT_NOT_FOUND",
  INVALID_PROJECT_ID: "INVALID_PROJECT_ID",
  RUNNING: "RUNNING",
};

export type CacheProjectState = {
  status: CacheProjectStatus;
  progress: number;
  lastUpdated: number | null;
  projectId: string;
};

export interface CacheDataState {
  // Record<projectId, CacheStatus>
  mapProjectState: Record<string, CacheProjectState>;
}

type CacheStateActionType = "UPDATE_CACHE_STATUS" | "SET_MAP_PROJECT_STATE";

interface CacheStateActionMap {
  UPDATE_CACHE_STATUS: { id: string; data: Partial<CacheProjectState> };
  SET_MAP_PROJECT_STATE: Record<string, CacheProjectState>;
}

export type CacheStateAction = {
  [K in CacheStateActionType]: CacheStateActionMap[K] extends void
    ? { type: K; payload?: undefined }
    : { type: K; payload: CacheStateActionMap[K] };
}[CacheStateActionType];

export const DEFAULT_CACHE_DATA_STATE: CacheDataState = {
  mapProjectState: {},
};

export type ProjectResourceInfo = {
  resource: string;
  lastUpdated: number;
  numberRecord: number;
};
